<template>
  <div class="row groupcreate">
    <div class="column col-12">
      <div class="space-between-row">
        <div class="col-12 col-sm-6 col-md-4">
          <forminput
            v-model="group.name"
            v-validate="'required'"
            :hint="$t('groups.name')"
            name="group.name"
            :required="true"
            :is-valid="!errors.has('group.name')"
          />
        </div>
        <div class="simple-text right-outer">
          <div class="cap-label">
            {{ $t('groups.language') }}
          </div>
          <pillbox
            :items="languages"
            :selected="selectedlanguages"
            @change="onLanguageChanged"
          >
          </pillbox>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <textareainput
            v-model="group.description"
            :rows="4"
            :title="$t('groups.description')"
          />
        </div>
      </div>
      <div class="row">
        <div class="col-12 col-sm-6 col-md-4">
          <sprdropdownnumberkey
            v-model="group.category"
            v-validate="'required'"
            :title="$t('groups.category')"
            name="group.category"
            :items="groupCategories"
            :required="group.groupType === 1"
            :is-valid="!errors.has('group.category') || group.groupType !== 1"
          />
        </div>
      </div>
      <div class="row">
        <div
          v-if="group.groupType === 1 && externalFormsAvailable"
          class="col-12 col-sm-6 col-md-4"
        >
          <div class="cap-label">
            {{ $t('externalForm.addLinkTitle') }}
          </div>
          <dropdowninput
            v-model="newSelectedExternalForm"
            :title="$t('externalForm.addLinkTitle')"
            name="externalForm"
            :items="filteredExternalForms"
            :required="false"
            is-valid
            @change="onExternalFormSelected"
          />
        </div>
      </div>
      <div class="row">
        <div
          v-if="group.groupType === 1 && externalFormsAvailable"
          class="row selected-external-forms-list mt-2"
        >
          <SelectedExternalForm
            v-for="form in selectedExternalForms"
            :key="form.id"
            :external-form="form"
            @remove="removeExternalForm(form.id)"
          />
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <sprcheckbox
            v-model="group.isAllLocations"
            :title="$t('groups.isAllLocations').toString()"
            :aria-label="$t('groups.isAllLocations').toString()"
          />
        </div>
      </div>
      <div v-if="isActionGroup()" class="row">
        <div class="col-12">
          <sprcheckbox
            v-model="group.isOnline"
            :title="$t('groups.isOnline').toString()"
            :aria-label="$t('groups.isOnline').toString()"
          />
        </div>
      </div>
      <!-- Group Organizer -->
      <div v-if="isActionGroup()">
        <div class="row grouporganizer">
          <div class="col-12">
            <groupOrganizer
              v-validate="'hasOptionalGroupOrganizer'"
              :hint="$t('userAdmin.groupOrganizer').toString()"
              name="userAdmin.groupOrganizer"
              :value="group.groupOrganizer"
              :required="false"
              :is-valid="!errors.has('userAdmin.groupOrganizer')"
              @input="onGroupOrganizerChanged"
            />
          </div>
        </div>
      </div>
      <div class="row button-row">
        <div class="col-12">
          <div class="d-flex justify-content-end">
            <sprbutton
              type="button"
              :size="4"
              :title="$t('groups.cancel')"
              class="spr-redborder button-width"
              click-event="cancelSave"
              @cancelSave="onCancelCreate"
            />
            <sprbutton
              type="submit"
              :size="4"
              :title="$t('groups.save')"
              icon="chevron-right"
              class="spr-redborder button-width"
              click-event="createGroup"
              @createGroup="onCreateGroup"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import * as _ from 'lodash'

import sprbutton from '../SPRButton.vue'
import forminput from '../SPRSimpleText.vue'
import textareainput from '../SPRSimpleTextArea.vue'
import sprcheckbox from '../SPRSimpleCheckbox.vue'
import sprdropdownnumberkey from '../SPRDropdownNumberKey.vue'
import groupOrganizer from '../SPRGroupOrganizersDropdown.vue'
import dropdowninput from '../../components/common/Dropdown/SingleSelectDropdown.vue'
import pillbox from '../common/Pill/PillBox.vue'
import SelectedExternalForm from '@/components/useradmin/SelectedExternalForm.vue'

import { ExternalFormUpdateDto } from '@/types'

import { Validator } from 'vee-validate'
import hasOptionalGroupOrganizer from '../../mixins/validator-hasOptionalGroupOrganizer.js'

Validator.extend('hasOptionalGroupOrganizer', hasOptionalGroupOrganizer)

interface GroupOrganizer {
  groupOrganizerType: number
  organizerId: number | null
}

interface Group {
  name: string
  description: string
  groupCreatorId: number
  members: any[]
  isAllLocations: boolean
  isOnline: boolean
  sectionId: number
  selectedExternalForm?: any
  externalForms: ExternalFormUpdateDto[]
  districtId: number
  groupType: number
  category: number | null
  languages: number
  externalForm?: any
  groupOrganizer?: GroupOrganizer
}

export interface TemplateComponentData {
  group: Group
  languages: any
  selectedlanguages: any
  externalForms: Array<ExternalFormUpdateDto>
  selectedExternalForm: any
  selectedExternalForms: Array<{ id: number; text: string }>
  multiExternalForms: Array<ExternalFormUpdateDto>
  groupCategories: any
  newSelectedExternalForm: { id: number; text: string } | null
}

export default Vue.extend({
  name: 'GroupCreate',
  components: {
    sprbutton,
    textareainput,
    forminput,
    sprcheckbox,
    sprdropdownnumberkey,
    groupOrganizer,
    pillbox,
    dropdowninput,
    SelectedExternalForm,
  },
  props: {
    grouptype: {
      type: Number,
      default: 2,
    },
    isDistrictGroup: {
      type: Boolean,
      default: false,
    },
    isSectionGroup: {
      type: Boolean,
      default: false,
    },
  },
  data(): TemplateComponentData {
    return {
      group: {
        name: '',
        description: '',
        groupCreatorId: this.$store.state.profile.profileId,
        members: [],
        isAllLocations: false,
        isOnline: false,
        sectionId: this.isSectionGroup ? Number(this.$route.params.id) : -1,
        districtId: this.isDistrictGroup ? Number(this.$route.params.id) : -1,
        groupType: this.grouptype,
        selectedExternalForm: undefined,
        externalForms: [],
        category: null,
        languages: 0,
      },
      languages: [],
      selectedlanguages: [],
      externalForms: [],
      selectedExternalForm: '-1',
      selectedExternalForms: [],
      multiExternalForms: [],
      groupCategories: null,
      newSelectedExternalForm: null,
    }
  },
  computed: {
    filteredExternalForms(): Array<{ id: number; text: string }> {
      return this.externalForms
        .filter(
          (form) =>
            !this.selectedExternalForms.some((sel) => sel.id === form.id)
        )
        .map((form) => ({ id: form.id, text: form.name }))
    },
    externalFormsAvailable(): boolean {
      return this.externalForms && this.externalForms.length > 0
    },
  },
  mounted() {
    const lang = this.$store.state.customerLanguages
    this.languages = this.$store.state.groupLanguages
      .filter((l: any) => l.id !== 0)
      .map((v: any) => {
        return {
          id: v.id,
          text:
            'actionLanguage.' +
            lang.find(
              (l: any) => l.displayName.toUpperCase() === v.text.toUpperCase()
            ).isoCode,
        }
      })

    this.selectedlanguages = this.languages.filter((v) =>
      v.id === null || v.id === 0
        ? false
        : (v.id & this.group.languages) === v.id
    )
    if (this.group.externalForms?.length) {
      this.selectedExternalForms = this.group.externalForms.map((f) => {
        return { id: f.id, text: f.name }
      })
      this.multiExternalForms = this.group.externalForms
    }
  },
  created() {
    this.getCategories()
    this.fetchExternalForms()

    this.setDefaultGroupOrganizer()
  },
  methods: {
    onLanguageChanged(e: Array<{ id: number; text: string }>) {
      this.group.languages = e.reduce((b, v) => b + v.id, 0)
      this.selectedlanguages = e
    },
    fetchExternalForms() {
      try {
        const url =
          this.$store.state.apiBases.externalformservice +
          this.$store.state.apiEndpoints.externalformsall[0]
        const response = this.$http.get(url).then((response) => {
          this.externalForms = response.data as Array<ExternalFormUpdateDto>
          this.multiExternalForms = response.data.map((v) => {
            return { id: v.id, text: v.name }
          })
          if (this.group.externalForm) {
            this.selectedExternalForm = this.group.externalForm.id.toString()
          } else {
            this.selectedExternalForm = '-1'
          }
        })
      } catch (error) {
        console.error('Error fetching external forms:', error)
      }
    },
    getCategories() {
      const self = this
      this.groupCategories = _.map(
        this.$store.state.eventCategories,
        function (val: any) {
          return { value: val.id, text: self.$t('eventCategory.' + val.text) }
        }
      )
    },
    isActionGroup(): boolean {
      return this.group.groupType === 1 // ActionGroup
    },
    onGroupOrganizerChanged(groupOrganizer: GroupOrganizer) {
      this.group.groupOrganizer = groupOrganizer
    },
    setDefaultGroupOrganizer() {
      if (this.isActionGroup()) {
        if (this.group.sectionId === -1) {
          this.group.groupOrganizer = {
            groupOrganizerType: 100, // District
            organizerId: this.group.districtId,
          }
        } else {
          this.group.groupOrganizer = {
            groupOrganizerType: 200, // Section
            organizerId: this.group.sectionId,
          }
        }
      } else {
        this.group.groupOrganizer = {
          groupOrganizerType: -1,
          organizerId: null,
        }
      }
    },
    onCreateGroup() {
      this.$validator.validateAll().then((resp: boolean) => {
        if (resp) {
          this.$emit('save', this.group)
        }
      })
    },
    onExternalFormSelected(selected: { id: number; text: string }) {
      if (!selected || typeof selected.id !== 'number') return

      // Check if already added
      if (this.selectedExternalForms.some((f) => f.id === selected.id)) return

      // Add to selected list
      this.selectedExternalForms.push({ id: selected.id, text: selected.text })

      // Sync with group.externalForms
      const fullForm = this.externalForms.find((f) => f.id === selected.id)
      if (fullForm) {
        this.group.externalForms.push(fullForm)
      }

      // Reset selected item
      this.newSelectedExternalForm = null
    },
    removeExternalForm(id: number) {
      this.selectedExternalForms = this.selectedExternalForms.filter(
        (f) => f.id !== id
      )
      this.group.externalForms = this.group.externalForms.filter(
        (f) => f.id !== id
      )
    },

    onCancelCreate() {
      this.$emit('cancel', this.group)
    },
  },
})
</script>

<style lang="scss">
@import '~@/assets/scss/_variables.scss';
.space-between-row {
  display: flex;
  justify-content: space-between;
}
.groupcreate {
  background-color: $brand-white;
  padding: 20px 0;

  .button-row {
    button:first-child {
      margin-right: 5px;
    }
  }
}
</style>
